// src/domains/core/context-intelligence/engine/ruleEngine.ts

import type {
  Rule,
  RuleExecutionResult,
  RuleCondition,
  RuleConditionOperand,
} from "../types/rules.types";

import { ruleRegistry } from "../registry/ruleRegistry";

/**
 * Rule Engine
 * Executes declarative rules against current indicator data
 */
export const RuleEngine = {
  /**
   * Evaluate a single rule condition operand
   *
   * @param operand
   *
   * @param currentValue
   *
   * @returns {object} Evaluation result with satisfied flag and value
   *
   */
  evaluateOperand(
    operand: RuleConditionOperand,
    currentValue: null | number,
  ): { satisfied: boolean; value: null | number } {
    if (currentValue === null) {
      return { satisfied: false, value: null };
    }

    const { operator, threshold } = operand;

    switch (operator) {
      case "<": {
        return {
          satisfied: currentValue < (threshold as number),
          value: currentValue,
        };
      }
      case "<=": {
        return {
          satisfied: currentValue <= (threshold as number),
          value: currentValue,
        };
      }
      case ">": {
        return {
          satisfied: currentValue > (threshold as number),
          value: currentValue,
        };
      }
      case ">=": {
        return {
          satisfied: currentValue >= (threshold as number),
          value: currentValue,
        };
      }
      case "==": {
        return { satisfied: currentValue === threshold, value: currentValue };
      }
      case "!=": {
        return { satisfied: currentValue !== threshold, value: currentValue };
      }
      case "between": {
        // Reason: Array length check is necessary to ensure threshold has exactly 2 elements

        if (Array.isArray(threshold) && threshold.length === 2) {
          return {
            satisfied: currentValue >= threshold[0] && currentValue <= threshold[1],
            value: currentValue,
          };
        }
        return { satisfied: false, value: currentValue };
      }
      // Note: 'trending-up', 'trending-down', 'stable', 'changed-by', 'percent-change'
      // would require historical data - to be implemented in Phase 2
      default: {
        return { satisfied: false, value: currentValue };
      }
    }
  },

  /**
   * Evaluate a rule condition
   *
   * @param condition
   *
   * @param getIndicatorValue
   *
   * @returns {object} Condition evaluation result with satisfied flag and results
   *
   */
  evaluateCondition(
    condition: RuleCondition,
    getIndicatorValue: (indicatorId: string) => null | number,
  ): {
    satisfied: boolean;
    results: {
      operand: RuleCondition | RuleConditionOperand;
      satisfied: boolean;
      value?: number;
    }[];
  } {
    const { operands, operator } = condition;
    const results: {
      operand: RuleCondition | RuleConditionOperand;
      satisfied: boolean;
      value?: number;
    }[] = [];

    for (const operand of operands) {
      if ("indicatorId" in operand) {
        // It's a RuleConditionOperand
        const currentValue = getIndicatorValue(operand.indicatorId);
        const result = RuleEngine.evaluateOperand(operand, currentValue);

        results.push({
          operand,
          satisfied: result.satisfied,
          ...(result.value !== null && { value: result.value }),
        });
      } else {
        // It's a nested RuleCondition
        const nestedResult = RuleEngine.evaluateCondition(operand, getIndicatorValue);
        results.push(...nestedResult.results);
      }
    }

    // Combine results based on operator
    let satisfied = false;
    switch (operator) {
      case "AND": {
        satisfied = results.every((r) => r.satisfied);
        break;
      }
      case "OR": {
        satisfied = results.some((r) => r.satisfied);
        break;
      }
      case "NOT": {
        satisfied = !results.every((r) => r.satisfied);
        break;
      }
      case "XOR": {
        const trueCount = results.filter((r) => r.satisfied).length;
        satisfied = trueCount === 1;
        break;
      }
    }

    return { results, satisfied };
  },

  /**
   * Execute a single rule
   *
   * @param rule
   *
   * @param getIndicatorValue
   *
   * @returns {Promise<RuleExecutionResult>} Rule execution result
   *
   */
  executeRule(
    rule: Rule,
    getIndicatorValue: (indicatorId: string) => null | number,
  ): RuleExecutionResult {
    const startTime = Date.now();

    try {
      // Check cooldown
      if (rule.cooldown && rule.lastExecuted) {
        const timeSinceLastExecution = Date.now() - rule.lastExecuted;
        if (timeSinceLastExecution < rule.cooldown) {
          return {
            executedAt: Date.now(),
            executionTime: Date.now() - startTime,
            rule,
            satisfied: false,
          };
        }
      }

      // Evaluate condition
      const conditionResult = RuleEngine.evaluateCondition(rule.condition, getIndicatorValue);
      const satisfied = conditionResult.satisfied;

      // Update rule execution metadata
      ruleRegistry.updateExecution(rule.id, satisfied);

      return {
        conditionResults: conditionResult.results,
        executedAt: Date.now(),
        executionTime: Date.now() - startTime,
        rule,
        satisfied,
      };
    } catch (error) {
      return {
        actionResult: {
          error: error instanceof Error ? error.message : "Unknown error",
          success: false,
        },
        executedAt: Date.now(),
        executionTime: Date.now() - startTime,
        rule,
        satisfied: false,
      };
    }
  },

  /**
   * Execute all enabled rules
   *
   * @param getIndicatorValue
   *
   * @returns {Promise<RuleExecutionResult[]>} Array of rule execution results
   *
   */
  async executeAllRules(
    getIndicatorValue: (indicatorId: string) => null | number,
  ): Promise<RuleExecutionResult[]> {
    const rules = ruleRegistry.getByPriority();
    const results = await Promise.all(
      rules.map((rule) => RuleEngine.executeRule(rule, getIndicatorValue)),
    );
    return results;
  },

  /**
   * Execute rules that are satisfied
   * Returns only rules where condition is met
   *
   * @param getIndicatorValue
   *
   * @returns {Promise<RuleExecutionResult[]>} Array of satisfied rule execution results
   *
   */
  async executeSatisfiedRules(
    getIndicatorValue: (indicatorId: string) => null | number,
  ): Promise<RuleExecutionResult[]> {
    const allResults = await this.executeAllRules(getIndicatorValue);
    return allResults.filter((result) => result.satisfied);
  },
};

/**
 * Create indicator value getter function
 * This combines both adapters to provide a unified interface
 *
 * Note: This is a simplified version for PoC
 * In production, this would need to be called from a React component context
 * or use a different pattern for accessing hooks
 *
 * @param getHealthValue
 *
 * @param getProductivityValue
 *
 * @returns {(indicatorId: string) => null | number} Indicator value getter function
 *
 */
export function createIndicatorValueGetter(
  getHealthValue: (indicatorId: string) => null | number,
  getProductivityValue: (indicatorId: string) => null | number,
): (indicatorId: string) => null | number {
  return (indicatorId: string): null | number => {
    // Try health adapter first
    const healthValue = getHealthValue(indicatorId);
    if (healthValue !== null) {
      return healthValue;
    }

    // Try productivity adapter
    const productivityValue = getProductivityValue(indicatorId);
    if (productivityValue !== null) {
      return productivityValue;
    }

    return null;
  };
}
